/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from 'react';
import ManageControls from 'src/components/manage-controls.jsx';
import { addClasses } from 'lib/helpers/class.js';
import { DetailButtons } from 'src/components/control-buttons.jsx';
import Button from '#lib/components/button.jsx';
import { cn } from '#lib/helpers/class.js';
import DropdownPublish from '#components/dropdown-publish.jsx';
import { Icon } from '@iconify-icon/react';
import XCircleIcon from '@heroicons/react/24/outline/esm/XCircleIcon';
import EyeIcon from '@heroicons/react/24/outline/esm/EyeIcon';
import ShareIcon from '@heroicons/react/24/outline/esm/ShareIcon';
import { useMobileScreen } from '#hooks/use-mobile-screen.js';
import { usePurchases } from '#src/@billing/use-billing.js';
import { SharePanel } from '#components/share-panel.jsx';
import { formatReadableDateAndHours } from '#helpers/helpers.js';
import useEffectAfterMount from '#hooks/global/use-effect-after-mount.js';
import { useRouteLoaderData } from 'react-router-dom';
import useAuth from '#lib/hooks/use-auth.jsx';

export function ActionButtons({
    crud,
    form,
    id,
    isCreate,
    isImported,
    isPublished,
    isLoadingPublish,
    publish,
    unpublish,
    onPublish,
    onUnPublish,
    publishedLink,
    isOpen,
    onOpen,
    onClose,
    onUpdateItinerary,
    refreshLoading,
    lastPublish,
    setIsSaving,
    isSaving,
    activeProfile,
    handleGenerateDescription,
    description
}) {
    const {
        meta: { data: userData }
    } = useAuth();
    const { isMobile } = useMobileScreen({ defaultInnerWidth: 1024 });
    const { data: purchases, isLoading } = usePurchases();
    const buttonRef = useRef(null);
    useEffectAfterMount(() => {
        if (isSaving === false) onPublish();
    }, [isSaving]);

    const handleClick = () => {
        buttonRef.current.click();
        setIsSaving(true);
    };

    if (isLoading) return;
    let shareable = false;
    const premiumPurchases = purchases?.filter(
        (purchase) => purchase.packageId === 'PREMIUM' || purchase.packageId === 'UNLIMITED'
    );

    premiumPurchases?.forEach((purchase) => {
        if (shareable) return;
        const now = new Date();
        const then = new Date(purchase.datePurchase);
        const diffInDays = Math.abs(Math.round((then - now) / (1000 * 60 * 60 * 24)));
        shareable = diffInDays < 365;
    });
    const handlePublish = () => {
        if (!description) if (confirm(_t.edit.missingDescriptionWarning())) return handleGenerateDescription(false);
        handleClick();
    };

    return (
        <ManageControls.ButtonSlot>
            <DetailButtons
                clone={{
                    busy: crud.clone.isLoading,
                    onClick: crud.clone.mutate
                }}
                save={{
                    busy: crud.save.isLoading,
                    ...form.props.submit,
                    disabled: form.data.isBlocked && !userData.mainUser
                }}
                delete={{
                    onDelete: crud.delete.mutate,
                    busy: crud.delete.isLoading,
                    disabled: form.data.isBlocked && !userData.mainUser
                }}
                add={true}
                changeTracker={{ rowKey: id }}
                busy={
                    crud.clone.isLoading ||
                    crud.delete.isLoading ||
                    refreshLoading ||
                    isLoadingPublish ||
                    unpublish.isLoading
                }
            >
                {isImported && (
                    <Button
                        onClick={onUpdateItinerary}
                        look="white"
                        size="xs"
                        disabled={refreshLoading}
                        className={addClasses(
                            cn(
                                'text-sm text-neutral-700 font-semibold ',
                                isMobile && 'flex items-start hover:text-primary-700'
                            )
                        )}
                        busy={refreshLoading}
                    >
                        <Icon icon="tabler:arrows-diff" width="1.2rem" height="1.2rem" className="mr-1" />
                        {_t.edit.updateItinerary.updateBtn()}
                    </Button>
                )}

                {!isCreate && !isPublished && (
                    <Button
                        look={!isMobile ? 'primary' : 'text'}
                        onClick={handlePublish}
                        size="xs"
                        disabled={isLoadingPublish}
                        className={cn(isMobile && 'flex items-start hover:text-primary-700')}
                        busy={isLoadingPublish}
                    >
                        <Icon icon="line-md:upload-outline-loop" width="1.2rem" height="1.2rem" className="mr-1" />
                        {_t.crud.itinerary.publish()}
                    </Button>
                )}
                <input ref={buttonRef} className="hidden" {...form.props.submit}></input>
                {isPublished && !isMobile && (
                    <DropdownPublish
                        publish={publish}
                        onRemovePublication={onUnPublish}
                        removePublication={unpublish}
                        publishedLink={publishedLink}
                        publishLoading={isLoadingPublish}
                        removeLoading={unpublish.isLoading}
                        busy={isLoadingPublish || unpublish.isLoading}
                        onOpen={onOpen}
                        lastPublish={lastPublish}
                        id={id}
                        handlePublish={handlePublish}
                    ></DropdownPublish>
                )}

                {isPublished && isMobile && (
                    <Button
                        look="text"
                        className={addClasses(
                            'w-full text-sm font-semibold text-red-700 hover:text-red-600 flex items-start border-t pt-2'
                        )}
                        onClick={onUnPublish}
                        size="xs"
                        disabled={unpublish.isLoading}
                        busy={unpublish.isLoading}
                    >
                        <XCircleIcon className="icon-sm mr-1" />
                        {_t.crud.itinerary.unpublish()}
                    </Button>
                )}
                {isPublished && isMobile && (
                    <Button
                        look="text"
                        size="xs"
                        className={addClasses(
                            'w-full text-sm font-semibold text-neutral-700 flex items-start hover:text-primary-700'
                        )}
                        // TODO: change url in production
                        href={publishedLink}
                        target="_blank"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <EyeIcon className="icon-sm mr-1" />
                        {_t.crud.itinerary.openItinerary()}
                    </Button>
                )}
                {isPublished && isMobile && (
                    <Button
                        look="text"
                        onClick={onOpen}
                        size="xs"
                        className={addClasses(
                            'w-full text-sm font-semibold text-neutral-700 hover:text-primary-700 flex items-start'
                        )}
                    >
                        <ShareIcon width={18} height={18} className="mr-1" />
                        {_t.crud.itinerary.share()}
                    </Button>
                )}
                {isOpen && (
                    <SharePanel
                        defaultEmail={form.data.notificationsEmail}
                        isOpen={isOpen}
                        onClose={onClose}
                        publishedLink={publishedLink}
                        shareable={shareable}
                        id={id}
                        activeProfile={activeProfile}
                    />
                )}
                {isPublished && isMobile && (
                    <Button
                        look={!isMobile ? 'primary' : 'text'}
                        onClick={handlePublish}
                        size="xs"
                        disabled={isLoadingPublish}
                        className={addClasses(
                            'w-full text-sm font-semibold text-neutral-700 flex items-start hover:text-primary-700'
                        )}
                        busy={isLoadingPublish}
                    >
                        <Icon icon="line-md:upload-outline-loop" width="1.2rem" height="1.2rem" className="mr-1" />
                        {_t.crud.itinerary.publish()}
                    </Button>
                )}
                {isPublished && isMobile && (
                    <div>
                        <div className="text-xs text-neutral-500 border-b pb-1">
                            {`${_t.edit.fields.lastPublication()} ${formatReadableDateAndHours(lastPublish)}`}
                        </div>
                    </div>
                )}
            </DetailButtons>
        </ManageControls.ButtonSlot>
    );
}
